import { makeAutoObservable, runInAction } from 'mobx';

import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { addMembersStore } from 'pages/Profile/components/AddMembersModal/AddMembersStore';
import { CREATE_USER_GROUP } from 'pages/Profile/gql/createGroup';
import { UPDATE_GROUP } from 'pages/Profile/gql/updateGroup';
import { groupsStore } from 'pages/Profile/GroupsStore';

const GROUP_DATA_DEFAULT = {
	name: '',
	description: '',
	id: null,
};

const groupNameAlreadyUsed = {
	header: 'Group name already in use',
	label: 'Please choose a different name - this one is already taken by another user',
	appearance: 'danger',
};

class ManageGroupDataStore {
	groupData = GROUP_DATA_DEFAULT;

	isActive = false;
	isLoading = false;
	isInCreationMode = false;
	isFromStepBack = false;

	constructor() {
		makeAutoObservable(this);
	}

	activateManageGroupDataModal = (activateWithEdit = false, groupData = null) => {
		this.isActive = true;
		this.isInCreationMode = !activateWithEdit;
		if (activateWithEdit && groupData !== null) {
			this.groupData = groupData;
		}
	};

	createNewGroup = async (payload) => {
		this.isLoading = true;
		const variables = {
			name: payload.name,
			description: payload.description,
		};
		try {
			const response = await client.mutate({
				mutation: CREATE_USER_GROUP,
				variables: variables,
				fetchPolicy: 'no-cache',
			});
			if (Object.hasOwn(response.data.createCrewMemberGroup, 'errors')) {
				notificationStore.setNotification(groupNameAlreadyUsed);
				runInAction(() => {
					this.isLoading = false;
				});
				return;
			}
			runInAction(() => {
				this.isLoading = false;
				this.isActive = false;
				this.isInCreationMode = false;
				this.groupData = response.data.createCrewMemberGroup.group;
			});
			addMembersStore.triggerAddMembers(response.data.createCrewMemberGroup.group.id, true);
			groupsStore.addGroup(response.data.createCrewMemberGroup.group);
		} catch (e) {
			console.error(e);
			notificationStore.setNotification(standardError);
			this.isLoading = false;
		}
	};

	updateGroup = async (payload) => {
		try {
			const response = await client.mutate({
				mutation: UPDATE_GROUP,
				variables: payload,
				fetchPolicy: 'no-cache',
			});
			if (Object.hasOwn(response.data.updateCrewMemberGroup, 'errors')) {
				notificationStore.setNotification(groupNameAlreadyUsed);
				runInAction(() => {
					this.isLoading = false;
				});
				return;
			}
			runInAction(() => {
				this.isLoading = false;
				this.isActive = false;
				this.groupData = response.data.updateCrewMemberGroup.group;
			});
			groupsStore.addGroup(response.data.updateCrewMemberGroup.group);
		} catch (e) {
			console.error(e);
			notificationStore.setNotification(standardError);
			this.isLoading = false;
		}
	};

	deactivateCreationFlow = () => {
		this.isActive = false;
		this.isInCreationMode = false;
		this.isFromStepBack = false;
		this.groupData = GROUP_DATA_DEFAULT;
	};

	deactivateEditFlow = () => {
		this.isActive = false;
		this.isFromStepBack = false;
		this.groupData = GROUP_DATA_DEFAULT;
	};

	activateFromStepBack = () => {
		this.isActive = true;
		this.isFromStepBack = true;
	};

	nextFromStepBack = () => {
		this.isLoading = false;
		this.isActive = false;
		this.isInCreationMode = false;
		addMembersStore.triggerAddMembers(this.groupData.id, true);
		this.isFromStepBack = false;
	};
}

export const manageGroupDataStore = new ManageGroupDataStore();
